const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export interface OutreachRecipient {
  coachName: string | null;
  coachEmail: string | null;
  schoolName: string;
  teamId?: string;
  division?: string;
}

export interface OutreachDetail {
  _id: string;
  recipient: OutreachRecipient;
  email: {
    subject: string;
    /** Plain text, derived on the server from richBody when richBody is set */
    body: string;
    /** Sanitized HTML of the body as edited (ZON-294); null on older drafts */
    richBody?: string | null;
    htmlBody?: string;
  };
  status: string;
  chatThreadId?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt?: string;
  firstOpenedAt?: string;
  firstClickedAt?: string;
  opens?: Array<{ timestamp: string; userAgent?: string }>;
  clicks?: Array<{ timestamp: string; url?: string }>;
}

export interface OutreachListResponse {
  outreach: OutreachDetail[];
  total: number;
  page: number;
  pages: number;
}

export async function fetchOutreach(params?: {
  status?: string;
  page?: number;
  limit?: number;
  linkedJgsProfile?: string;
}): Promise<OutreachListResponse> {
  const url = new URL(withBase("/outreach"));
  if (params?.status) url.searchParams.set("status", params.status);
  if (params?.page) url.searchParams.set("page", String(params.page));
  if (params?.limit) url.searchParams.set("limit", String(params.limit));
  if (params?.linkedJgsProfile) url.searchParams.set("linkedJgsProfile", params.linkedJgsProfile);

  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch outreach (${resp.status})`);
  }

  return resp.json();
}

export async function fetchOutreachDetail(
  id: string,
  linkedJgsProfile?: string
): Promise<OutreachDetail> {
  const url = new URL(withBase(`/outreach/${encodeURIComponent(id)}`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);
  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch outreach detail (${resp.status})`);
  }

  return resp.json();
}

export async function updateOutreachDraft(
  id: string,
  payload: { subject?: string; body?: string; richBody?: string; coachEmail?: string },
  linkedJgsProfile?: string
): Promise<OutreachDetail> {
  const url = new URL(withBase(`/outreach/${encodeURIComponent(id)}`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);
  const resp = await fetch(url.toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to update outreach (${resp.status})`);
  }

  return resp.json();
}

export interface OutreachSender {
  fromName: string;
  fromAddress: string;
  replyTo: string;
  loginEmail: string;
  nameSource: "profile" | "ranking" | "account" | "fallback";
  replyToSource: "profile" | "login";
}

/** How this player's coach emails go out: From name/address and reply-to (ZON-291). */
export async function fetchOutreachSender(): Promise<OutreachSender> {
  const resp = await fetch(withBase("/outreach/sender"), { credentials: "include" });
  if (!resp.ok) throw new Error(`Failed to load sender details (${resp.status})`);
  return resp.json();
}

export async function sendOutreach(id: string, linkedJgsProfile?: string): Promise<OutreachDetail> {
  const url = new URL(withBase(`/outreach/${encodeURIComponent(id)}/send`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);
  const resp = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to send outreach (${resp.status})`);
  }

  return resp.json();
}

export async function deleteOutreach(id: string, linkedJgsProfile?: string): Promise<void> {
  const url = new URL(withBase(`/outreach/${encodeURIComponent(id)}`));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);
  const resp = await fetch(url.toString(), {
    method: "DELETE",
    credentials: "include",
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to delete outreach (${resp.status})`);
  }
}
